import { useCallback, useEffect, useRef } from 'react';

interface UseLongPressOptions {
  onLongPress: () => void;
  delayMs?: number;
}

export interface LongPressHandlers {
  onMouseDown: () => void;
  onMouseUp: () => void;
  onMouseLeave: () => void;
  onTouchStart: () => void;
  onTouchEnd: () => void;
  onTouchMove: () => void;
  onTouchCancel: () => void;
}

interface UseLongPressResult {
  handlers: LongPressHandlers;
  /** True if the most-recent press triggered a long press; consult inside the click handler to suppress the click. */
  wasLongPress: () => boolean;
  /** Clear the long-press latch (call after consuming wasLongPress). */
  resetLongPress: () => void;
}

const DEFAULT_DELAY_MS = 500;

export const useLongPress = ({
  onLongPress,
  delayMs = DEFAULT_DELAY_MS,
}: UseLongPressOptions): UseLongPressResult => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggeredRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const fire = useCallback(() => {
    triggeredRef.current = true;
    onLongPress();
  }, [onLongPress]);

  const handlePressStart = useCallback(() => {
    triggeredRef.current = false;
    clearTimer();
    timerRef.current = setTimeout(fire, delayMs);
  }, [clearTimer, fire, delayMs]);

  const handlePressEnd = useCallback(() => {
    clearTimer();
    setTimeout(() => {
      triggeredRef.current = false;
    }, 0);
  }, [clearTimer]);

  useEffect(() => clearTimer, [clearTimer]);

  return {
    handlers: {
      onMouseDown: handlePressStart,
      onMouseUp: handlePressEnd,
      onMouseLeave: clearTimer,
      onTouchStart: handlePressStart,
      onTouchEnd: handlePressEnd,
      onTouchMove: clearTimer,
      onTouchCancel: clearTimer,
    },
    wasLongPress: () => triggeredRef.current,
    resetLongPress: () => {
      triggeredRef.current = false;
    },
  };
};
